import React, { FC, useEffect, useState } from "react";
import queryString from "query-string";
import SofIcon from "../common/Icons";
import ToastContent from "../common/ToastContent";
import { connect } from "react-redux";
import { ExecuteActionResult } from "../../models";
import { mergeStyleSets } from "@fluentui/react";
import { Panel } from "@fluentui/react";
import { PanelType } from "@fluentui/react";
import { State } from "../../store";
import { DetailsList } from "office-ui-fabric-react/lib/DetailsList";
import { IColumn } from "office-ui-fabric-react/lib/DetailsList";
import { SelectionMode } from "office-ui-fabric-react/lib/DetailsList";
import { last6Digits } from "../../services/Helper";
import { LineHandlingApi } from "../../api/lineHandlingApi";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ActivityService } from "../../services/ActivityService";
import * as ActivityLogStore from "../../store/ActivityLogsStore";
import * as GeneralStore from "../../store/GeneralStore";
import * as OfflineDataStore from "../../store/OfflineDataStore";
import * as LineHandlingStore from "../../store/LineHandlingStore";
import { WorkPacketService } from "../../services/WorkPacketService";
import { OfflineWorkPacketLog } from "../../models/OfflineDataHistory";
import { WorkPacketSyncActionResult } from "../../models/WorkPacket";

const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: process.env.REACT_APP_INSTRUMENTATION_KEY
  }
});
appInsights.loadAppInsights();

const errorSync = "Unable to sync data";
type Props = OfflineDataStore.OfflineDataState &
  LineHandlingStore.LineHandlingState &
  typeof ActivityLogStore.actionCreators &
  typeof GeneralStore.actionCreators &
  typeof LineHandlingStore.actionCreators &
  typeof OfflineDataStore.actionCreators;
const classes = mergeStyleSets({
  panel: {
    width: 652
  },
  callout: {
    width: "300px",
    padding: "12px"
  },
  offlineButton: {
    margin: "0 12px",
    height: "32px !important",
    color: "#F0990D"
  },
  offlineIcon: {
    color: "#F0990D !important",
    fontSize: "24px !important"
  },
  offlinePanelBody: {
    margin: "8px 0"
  },
  offlinePanelFooter: {
    textAlign: "right"
  },
  header: {
    color: "#12427D",
    fontSize: "24px"
  },
  cloudIcon: {
    margin: "auto",
    display: "block"
  }
});
type OfflineAction = {
  lineHandlingNo: string;
  nominationNo?: string;
  actionName: string;
};

const OfflineDataPane: FC<Props> = (props: Props) => {
  const { offlineData } = props;
  const [showPanel, togglePanel] = useState(false);
  const [isSynching, setIsSynching] = useState(false);
  const [syncResult, setSyncResult] = useState<ExecuteActionResult[]>([]);
  const [syncDWPResult, setSyncDWPResult] = useState<
    WorkPacketSyncActionResult[]
  >([]);

  const [svlSyncResult, setSvlSyncResult] = useState<any[]>([]);
  const history = useHistory();
  const [isOffline, setIsOffline] = useState(false);

  const svlColumns: IColumn[] = [
    {
      key: "lineHandling",
      name: "Line Handling",
      data: "string",
      fieldName: "lineHandlingNo",
      minWidth: 80
    },
    {
      key: "nomination",
      name: "Nomination",
      data: "string",
      fieldName: "nominationNo",
      minWidth: 80
    },
    {
      key: "shipVaporLine",
      name: "Ship Vapor Line",
      data: "string",
      fieldName: "action",
      minWidth: 120
    }
  ];
  const columns: IColumn[] = [
    {
      key: "lineHandling",
      name: "Line Handling",
      data: "string",
      fieldName: "lineHandlingNo",
      minWidth: 80
    },
    {
      key: "nomination",
      name: "Nomination",
      data: "string",
      fieldName: "nominationNo",
      minWidth: 80
    },
    {
      key: "action",
      name: "Action",
      data: "string",
      fieldName: "action",
      minWidth: 80
    },
    {
      key: "status",
      name: "Status",
      minWidth: 62,
      onRender: (item?: any) => (item.isSuccess ? "Synced" : "Skipped")
    }
  ];
  const dwpColumns: IColumn[] = [
    {
      key: "lineHandling",
      name: "Line Handling",
      data: "string",
      fieldName: "lineHandlingNo",
      minWidth: 30
    },
    {
      key: "workPacketName",
      name: "Work Packet",
      data: "string",
      fieldName: "workPacketName",
      minWidth: 150
    },
    {
      key: "status",
      name: "Status",
      minWidth: 62,
      onRender: (item?: any) => (item.isSuccess ? "Synced" : "Skipped")
    }
  ];

  const syncActivityLogs = async () => {
    const activityService = new ActivityService();
    const workPacketService = new WorkPacketService();
    const { offlineData, offlineSVLData, offlineWorkPacketData } = props;

    const overallSVLResult = [];
    if (offlineSVLData && !isSynching && offlineSVLData.length > 0) {
      for (const svlData of offlineSVLData) {
        setIsSynching(true);
        const result = await LineHandlingApi.updateShipVaporLineAsync(
          svlData.lineHandlingNo,
          svlData.nominations
        );
        setIsSynching(false);
        if (result.ok) {
          props.removeSVLOfflineData(svlData.lineHandlingNo);
          overallSVLResult.push(
            ...svlData.nominations.map((n, index) => ({
              key: index,
              lineHandlingNo: last6Digits(svlData.lineHandlingNo),
              nominationNo: last6Digits(n.nominationNo),
              action:
                n.shipVaporLine +
                (n.shipVaporLineDescription
                  ? " - " + n.shipVaporLineDescription
                  : "")
            }))
          );
        }
      }
    }
    if (overallSVLResult.length > 0) {
      setSvlSyncResult(overallSVLResult);
    } else {
      setSvlSyncResult([]);
    }

    const overallResult = [];
    if (offlineData && !isSynching && offlineData.length > 0) {
      for (const lineHandlingData of offlineData) {
        setIsSynching(true);
        const result = await activityService.executeActionsAsync(
          lineHandlingData
        );
        setIsSynching(false);
        if (result) {
          overallResult.push(result);
          updateView();
          props.removeOfflineData(lineHandlingData.lineHandlingNo);
        }
      }

      if (overallResult.length > 0) {
        let summary: ExecuteActionResult[] = [];
        for (const _res of overallResult) {
          summary.push(
            ..._res.summary.map(
              (f, index) =>
                ({
                  key: index,
                  lineHandlingNo: last6Digits(f.lineHandlingNo),
                  nominationNo: last6Digits(f.nominationNo),
                  action: f.action,
                  isSuccess: f.isSuccess
                } as ExecuteActionResult)
            )
          );
        }
        setSyncResult(summary);
        toast.info(
          <ToastContent
            title="Sync complete"
            content={overallResult[0].description}
            buttons={[
              { text: "See Details", action: () => togglePanel(true) },
              { text: "Dismiss", action: () => toast.dismiss() }
            ]}
          />,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined
          }
        );
      }
    }

    const overallWorkPacketResult = [];
    if (
      offlineWorkPacketData &&
      !isSynching &&
      offlineWorkPacketData.length > 0
    ) {
      for (const workPacketData of offlineWorkPacketData) {
        setIsSynching(true);
        const result = await workPacketService.executeActionsAsync(
          workPacketData
        );
        setIsSynching(false);
        if (result) {
          overallWorkPacketResult.push({
            lineHandlingNo: last6Digits(workPacketData.lineHandlingNo),
            workPacketId: workPacketData.workPacketId,
            workPacketName: workPacketData.name,
            isSuccess: true
          });
          // overallWorkPacketResult.push(result);
          updateView();
          props.removeOfflineWorkPacketData(
            workPacketData.lineHandlingNo,
            workPacketData.workPacketId
          );
        }
      }

      if (overallWorkPacketResult.length > 0) {
        let summary: WorkPacketSyncActionResult[] = overallWorkPacketResult;
        setSyncDWPResult(summary);
        toast.info(
          <ToastContent
            title="Sync complete"
            content={""}
            buttons={[
              { text: "See Details", action: () => togglePanel(true) },
              { text: "Dismiss", action: () => toast.dismiss() }
            ]}
          />,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined
          }
        );
      }
    }
  };

  const updateView = () => {
    const { search } = history.location;
    const q = queryString.parse(search.toLowerCase());
    const linehandlingno = q.linehandling as string;
    const nominationno = q.nomination as string | undefined;
    const lineHandlingNo =
      props.lineHandling.result && props.lineHandling.result.lineHandlingNo;

    if (
      linehandlingno !== undefined &&
      lineHandlingNo !== undefined &&
      linehandlingno.toUpperCase() === lineHandlingNo.toUpperCase()
    ) {
      props.getActivityLogs(lineHandlingNo, () => {
        props.getLineHandling(
          linehandlingno,
          nominationno,
          false,
          false,
          undefined
        );
      });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("Synching data..");
      syncActivityLogs();
    }, 5000);

    return () => clearTimeout(timer);
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetch("/api/health", {
        method: "GET"
      })
        .then((response) => {
          if (isOffline === true) {
            props.setOfflineStatus(false);
            setIsOffline(false);
            const { offlineData } = props;

            if (isSynching || offlineData.length > 0) {
              // empty
            } else {
              updateView();
            }
          }
        })
        .catch((err) => {
          if (isOffline === false) {
            props.setOfflineStatus(true);
            setIsOffline(true);

            toast.info(
              <ToastContent
                title="You are offline."
                content="Offline changes and activities will be synced when you go online."
                buttons={[{ text: "Dismiss", action: () => toast.dismiss() }]}
              />,
              {
                position: "bottom-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined
              }
            );
          }
        });
    }, 5000);
    return () => {
      window.clearInterval(timer);
    };
  }, [isOffline, isSynching]);

  return (
    <>
      {isOffline && <SofIcon iconname="Offline" />}

      {!isOffline && !isSynching && <SofIcon iconname="Online" />}

      {!isOffline && isSynching && <SofIcon iconname="Syncing" />}

      <Panel
        isOpen={showPanel}
        isLightDismiss
        onDismiss={() => togglePanel(false)}
        headerText="Sync Result"
        type={PanelType.custom}
        customWidth="430px"
        headerClassName={classes.header}
      >
        {svlSyncResult.length > 0 && (
          <DetailsList
            columns={svlColumns}
            selectionMode={SelectionMode.none}
            items={svlSyncResult}
            isHeaderVisible
          />
        )}
        {offlineData && (
          <DetailsList
            columns={columns}
            selectionMode={SelectionMode.none}
            items={syncResult}
            isHeaderVisible
          />
        )}
        {syncDWPResult.length > 0 && (
          <DetailsList
            columns={dwpColumns}
            selectionMode={SelectionMode.none}
            items={syncDWPResult}
            isHeaderVisible
          />
        )}
      </Panel>
    </>
  );
};
export default connect(
  (state: State) => ({
    ...state.offlineData,
    ...state.lineHandling
  }),
  {
    ...ActivityLogStore.actionCreators,
    ...GeneralStore.actionCreators,
    ...LineHandlingStore.actionCreators,
    ...OfflineDataStore.actionCreators
  }
)(OfflineDataPane as any);
